import { useCallback, useEffect } from "react";
import {
  CATEGORY_ID_FILTER,
  clearSearch,
  FilterProperties,
  loadSearchResponse,
  PartSearchSort,
  searchError,
  SearchParameters,
  SearchResponse,
  startSearch,
} from "../../reducers/componentSearch";
import { track } from "@amplitude/analytics-browser";
import settings from "../../constants/settings";
import useFetchAndTrace from "./fetchWithTrace";
import { useAppDispatch, useAppSelector } from "./store";
import { useRouter } from "next/router";
import _ from "lodash";
import { COMPONENTS } from "../../constants/routes";

const PAGE_QUERY = "page";
const SEARCH_TERMS_QUERY = "searchTerms";
const IN_STOCK_ONLY_QUERY = "inStockOnly";
const SORT_QUERY = "sort";

interface UpdateSearchParameters {
  page?: number;
  searchTerms?: string;
  categoryIds?: string[];
  filters?: FilterProperties;
  inStockOnly?: boolean;
  sort?: PartSearchSort | null;
}

interface UpdateSearchOptions {
  updateSearchParameters: UpdateSearchParameters;
  newSearch?: boolean;
  updatePath?: boolean;
}

const parameterToQuery = (parameterName: string, parameters?: string[]) => {
  if (parameters === undefined || parameters.length === 0) {
    return "";
  }
  return parameters
    .map((parameter) => `&${parameterName}=${parameter}`)
    .join("");
};

const sortToQuery = (sort: PartSearchSort | null) => {
  if (sort === null) {
    return "";
  }
  return `&${SORT_QUERY}=${sort}`;
};

const parseSortQuery = (
  sortQuery: string | string[] | undefined,
): PartSearchSort | null => {
  const sortValues = Object.values(PartSearchSort) as string[];
  if (typeof sortQuery === "string" && sortValues.includes(sortQuery)) {
    return sortQuery as PartSearchSort;
  }
  return null;
};

const filtersToQuery = (filters?: FilterProperties) => {
  if (filters === undefined || _.isEqual(filters, {})) {
    return "";
  }
  return Object.keys(filters)
    .map((parameterName) => {
      return parameterToQuery(parameterName, filters[parameterName]);
    })
    .join("");
};

const DEFAULT_PAGE = 1;
const DEFAULT_SEARCH_TERMS = "";
const DEFAULT_CATEGORY_IDS: string[] = [];
const DEFAULT_FILTERS: FilterProperties = {};
const DEFAULT_SEARCH_PARAMETERS: SearchParameters = {
  page: DEFAULT_PAGE,
  searchTerms: DEFAULT_SEARCH_TERMS,
  categoryIds: DEFAULT_CATEGORY_IDS,
  filters: DEFAULT_FILTERS,
  inStockOnly: false,
  sort: null,
};

const useGetSearchParameters = () => {
  const searchParameters = useAppSelector(
    (state) => state.componentSearch.searchParameters,
  );
  return useCallback(
    ({
      newSearch,
      updateSearchParameters,
    }: UpdateSearchOptions): SearchParameters => {
      const defaults =
        newSearch || searchParameters === null
          ? DEFAULT_SEARCH_PARAMETERS
          : searchParameters;

      return {
        ...defaults,
        ...Object.fromEntries(
          Object.entries(updateSearchParameters).filter(
            (entry) => entry[1] !== undefined,
          ),
        ),
      };
    },
    [searchParameters],
  );
};

export const useCreateSearchLink = () => {
  const getSearchParameters = useGetSearchParameters();

  return useCallback(
    (searchOptions: UpdateSearchOptions) => {
      const searchParameters = getSearchParameters(searchOptions);
      if (
        searchOptions.newSearch &&
        _.isEqual(searchParameters, DEFAULT_SEARCH_PARAMETERS)
      ) {
        return COMPONENTS;
      }
      const categoryIdsQuery = parameterToQuery(
        CATEGORY_ID_FILTER,
        searchParameters.categoryIds,
      );
      const filtersQuery = filtersToQuery(searchParameters.filters);
      const sortQuery = sortToQuery(searchParameters.sort);
      return `${COMPONENTS}?${PAGE_QUERY}=${searchParameters.page}&${SEARCH_TERMS_QUERY}=${searchParameters.searchTerms}${categoryIdsQuery}${filtersQuery}&${IN_STOCK_ONLY_QUERY}=${searchParameters.inStockOnly}${sortQuery}`;
    },
    [getSearchParameters],
  );
};

const useSearchParts = () => {
  const fetchAndTrace = useFetchAndTrace();
  const dispatch = useAppDispatch();
  const errorHandler = useCallback(() => {
    dispatch(searchError());
  }, [dispatch]);

  return useCallback(
    (searchParameters: SearchParameters) => {
      const { page, searchTerms, categoryIds, filters, inStockOnly, sort } =
        searchParameters;
      const searchHandler = async (response: Response) => {
        if (response.ok) {
          const newSearchResponse = (await response.json()) as SearchResponse;
          dispatch(
            loadSearchResponse({
              searchResponse: newSearchResponse,
            }),
          );
        } else {
          throw Error("Failed to search parts");
        }
      };

      track("Search inventory", {
        "search-terms": searchTerms,
        categoryIds,
        filters,
        page,
        inStockOnly,
        sort,
      });
      dispatch(startSearch({ searchParameters }));
      void fetchAndTrace(
        `${settings.api_url}/parts/search`,
        searchHandler,
        errorHandler,
        {
          body: JSON.stringify({
            search_terms: searchTerms,
            category_ids: categoryIds,
            filters: filters,
            page: page,
            in_stock_only: inStockOnly,
            sort: sort,
          }),
          method: "PUT",
        },
      );
    },
    [dispatch, errorHandler, fetchAndTrace],
  );
};

export const useRefreshSearch = () => {
  const searchParts = useSearchParts();
  const getSearchParameters = useGetSearchParameters();

  return useCallback(() => {
    searchParts(getSearchParameters({ updateSearchParameters: {} }));
  }, [getSearchParameters, searchParts]);
};

export const useUpdateSearch = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const createSearchLink = useCreateSearchLink();
  const searchParameters = useAppSelector(
    (state) => state.componentSearch.searchParameters,
  );
  const searchParts = useSearchParts();
  const getSearchParameters = useGetSearchParameters();

  return useCallback(
    (updateSearchOptions: UpdateSearchOptions) => {
      const newSearchParameters = getSearchParameters(updateSearchOptions);
      if (!_.isEqual(newSearchParameters, searchParameters)) {
        const searchIsEmpty = _.isEqual(
          newSearchParameters,
          DEFAULT_SEARCH_PARAMETERS,
        );
        if (router.pathname == COMPONENTS || updateSearchOptions.updatePath) {
          void router.push(
            searchIsEmpty ? COMPONENTS : createSearchLink(updateSearchOptions),
            undefined,
            {
              scroll: false,
            },
          );
        } else {
          if (searchIsEmpty) {
            dispatch(clearSearch());
          } else {
            searchParts(newSearchParameters);
          }
        }
      }
    },
    [
      dispatch,
      createSearchLink,
      getSearchParameters,
      router,
      searchParameters,
      searchParts,
    ],
  );
};

export const useQuerySearch = () => {
  const searchParts = useSearchParts();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const loading = useAppSelector((state) => state.componentSearch.loading);
  const errored = useAppSelector((state) => state.componentSearch.errored);
  const searchParameters = useAppSelector(
    (state) => state.componentSearch.searchParameters,
  );

  useEffect(() => {
    const queryIsIncomplete =
      router.query[SEARCH_TERMS_QUERY] === undefined ||
      router.query[PAGE_QUERY] === undefined;
    const newPage = parseInt(router.query[PAGE_QUERY] as string);
    const newSearchTerms = router.query[SEARCH_TERMS_QUERY] as string;
    const newInStockOnly: boolean = router.query[IN_STOCK_ONLY_QUERY] == "true";
    const newSort = parseSortQuery(router.query[SORT_QUERY]);
    const newFilters = Object.keys(router.query)
      .sort()
      .reduce((resultingFilters, key) => {
        if (
          key === PAGE_QUERY ||
          key === SEARCH_TERMS_QUERY ||
          key === CATEGORY_ID_FILTER ||
          key === IN_STOCK_ONLY_QUERY ||
          key === SORT_QUERY
        ) {
          return resultingFilters;
        } else {
          if (Array.isArray(router.query[key])) {
            resultingFilters[key] = [...router.query[key]].sort();
          } else if (router.query !== undefined) {
            resultingFilters[key] = [router.query[key] as string];
          }
          return resultingFilters;
        }
      }, {} as FilterProperties);
    const getCategoryIds = () => {
      if (router.query[CATEGORY_ID_FILTER] !== undefined) {
        if (Array.isArray(router.query[CATEGORY_ID_FILTER])) {
          return [...router.query[CATEGORY_ID_FILTER]];
        } else {
          return [router.query[CATEGORY_ID_FILTER]];
        }
      }
      return [];
    };
    const newCategoryIds = getCategoryIds();

    const shouldSearch = () => {
      if (loading || errored || queryIsIncomplete) {
        return false;
      }
      return (
        searchParameters?.page !== newPage ||
        searchParameters?.searchTerms !== newSearchTerms ||
        !_.isEqual(
          newCategoryIds.sort(),
          [...(searchParameters?.categoryIds || [])].sort(),
        ) ||
        !_.isEqual(newFilters, searchParameters.filters) ||
        searchParameters?.inStockOnly !== newInStockOnly ||
        searchParameters?.sort !== newSort
      );
    };

    if (shouldSearch()) {
      searchParts({
        page: newPage,
        searchTerms: newSearchTerms,
        categoryIds: newCategoryIds,
        filters: newFilters,
        inStockOnly: newInStockOnly,
        sort: newSort,
      });
    }
  }, [errored, loading, router.query, searchParameters, searchParts]);

  useEffect(() => {
    if (router.asPath === COMPONENTS) {
      dispatch(clearSearch());
    }
  }, [dispatch, router.asPath]);
};
